/**
 * GPX Calculations Utilities
 * Distance, duration, elevation, and center calculations for GPS tracks
 */

/**
 * Calculate distance between GPS points using Haversine formula
 * @param points Array of [latitude, longitude] coordinates
 * @returns Distance in meters
 */
export const calculateDistance = (points: [number, number][]): number => {
  if (points.length < 2) return 0;

  let totalDistance = 0;
  const R = 6371e3; // Earth's radius in meters

  for (let i = 0; i < points.length - 1; i++) {
    const φ1 = (points[i][0] * Math.PI) / 180;
    const φ2 = (points[i + 1][0] * Math.PI) / 180;
    const Δφ = ((points[i + 1][0] - points[i][0]) * Math.PI) / 180;
    const Δλ = ((points[i + 1][1] - points[i][1]) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    totalDistance += R * c;
  }

  return Math.round(totalDistance);
};

/**
 * Calculate duration from timestamps
 * @param timestamps Array of timestamp strings or Date objects
 * @returns Duration in seconds
 */
export const calculateDuration = (timestamps?: (string | Date)[]): number => {
  if (!timestamps || timestamps.length < 2) return 0;

  try {
    const startTime = new Date(timestamps[0]).getTime();
    const endTime = new Date(timestamps[timestamps.length - 1]).getTime();
    return Math.round((endTime - startTime) / 1000);
  } catch (e) {
    console.error("Error calculating duration:", e);
    return 0;
  }
};

/**
 * Calculate center point of a GPS track
 * @param points Array of [latitude, longitude] coordinates
 * @returns Center point as [latitude, longitude]
 */
export const calculateCenter = (
  points: [number, number][],
): [number, number] => {
  if (points.length === 0) return [46.2563, 5.6554]; // Default center (Oyonnax area)

  const avgLat =
    points.reduce((sum, point) => sum + point[0], 0) / points.length;
  const avgLon =
    points.reduce((sum, point) => sum + point[1], 0) / points.length;

  return [avgLat, avgLon];
};

/**
 * Calculate elevation gain from elevation data
 * @param elevations Array of elevation values in meters
 * @returns Total elevation gain in meters
 */
export const calculateElevationGain = (elevations: number[]): number => {
  if (elevations.length < 2) return 0;

  let totalGain = 0;
  for (let i = 1; i < elevations.length; i++) {
    const diff = elevations[i] - elevations[i - 1];
    if (diff > 0) {
      totalGain += diff;
    }
  }

  return Math.round(totalGain);
};
